import { SEGMENT_PROXY_ROUTES } from 'lib/consts/segmentProxy'

interface IsThirdPartyThrownErrorParams {
  source: string
  /** Datadog's serialized stack from the RUM error event. */
  stack?: string
  /** The original thrown value from the RUM error-event domain context. */
  error?: unknown
}

const VENDOR_THROWING_FRAME_PATTERNS: RegExp[] = [
  /^chrome-extension:\/\//,
  /^https:\/\/www\.gstatic\.com\/recaptcha\//,
  ...SEGMENT_PROXY_ROUTES.map(({ prefix }) => new RegExp(`^https://[^/]+${prefix}/`)),
]

// The frame shape comes from `toStackTraceString` in @datadog/browser-core — an internal
// serialization with no format contract. If an SDK upgrade changes it, every stack stops matching
// and the filter silently fails open, so re-verify the shape when bumping the SDK.
const THROWING_FRAME_RE = /^\s*at .*? @ (.+)$/m

// Datadog's stack parser (`computeStackTrace` in @datadog/browser-core) only keeps frames whose URL
// scheme it recognises, and these are not among them: a throw made entirely inside a Safari or
// Firefox extension reaches `beforeSend` with a serialized stack of zero frames, indistinguishable
// from an error that never had one. The raw `error.stack` still carries them. Safari and Firefox
// write frames only, `fn@url:line:col` with no `Name: message` header, so the first line is the
// throwing frame.
const MASKED_EXTENSION_TOP_FRAME_RE =
  /^[^\n@]*@(?:webkit-masked-url|safari-web-extension|safari-extension|moz-extension):\/\//

const getThrowingFrameUrl = (stack: string): string | undefined => THROWING_FRAME_RE.exec(stack)?.[1]?.trim()

const getRawStack = (error: unknown): string | undefined =>
  typeof error === 'object' && error !== null && 'stack' in error && typeof error.stack === 'string'
    ? error.stack
    : undefined

/**
 * True when RUM auto-captured an uncaught throw whose throwing (top) frame is vendor code we can't
 * act on: a browser extension, reCAPTCHA internals, or a Segment bundle behind the first-party
 * `/seg-cdn` proxy (our origin — only the path identifies it). Only the top frame decides:
 * extensions that patch `XMLHttpRequest` leave app frames beneath their throws, while an app throw
 * invoked *by* vendor code keeps an app top frame and stays reported. `logError` reports
 * (`source: custom`) never match.
 *
 * Chrome extension frames survive Datadog's serialization and are matched on `stack`; Safari and
 * Firefox extension frames don't, so those are matched on the raw stack of `error` instead.
 */
export const isThirdPartyThrownError = ({ source, stack, error }: IsThirdPartyThrownErrorParams): boolean => {
  if (source !== 'source') {
    return false
  }

  const rawStack = getRawStack(error)

  if (rawStack && MASKED_EXTENSION_TOP_FRAME_RE.test(rawStack)) {
    return true
  }

  if (!stack) {
    return false
  }

  const throwingFrameUrl = getThrowingFrameUrl(stack)

  if (!throwingFrameUrl) {
    return false
  }

  return VENDOR_THROWING_FRAME_PATTERNS.some((pattern) => pattern.test(throwingFrameUrl))
}
